import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { ClickAwayListener } from '@mui/material';

import { ChatChannelListContext } from '../../../chat';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import ChatContext from '../../context/ChatContext';
import LayoutContext from '../../layout/context/LayoutContext';
import QuickChatContainer from '../../pages/Chat/pages/QuickChat/QuickChatContainer';

import variants from './variants';
import {
  Container,
  ChatButton,
  StyledPopper,
  StyledChatLightIcon,
  StyledChatIcon,
  ChatIconButton,
  StyledChatTextIcon,
  StyledButton,
  UnreadBadge,
  UnreadBadgeText,
} from './styles';
import texts from './texts.json';

const ChatButtonComponent = ({
  userId,
  text,
  iconButton,
  smallButton,
  textIconButton,
  popperPlacement,
  iconVariant,
}) => {
  const [popperAnchor, setPopperAnchor] = useState(null);

  const { onQuickChatUserSelected, quickChatUser } = useContext(ChatContext);

  const { setIsChatPanelOpen, setRightPanelContent } = useContext(LayoutContext);

  const { setActiveUserId, userUnreadCounts } = useContext(ChatChannelListContext);

  const unreadCount = userUnreadCounts[userId] || 0;

  const handleQuickChatClick = useCallback((event) => {
    // Any action that opens chat from elsewhere should un-collapse the right panel and
    // switch it back to default chat view (not a custom right panel).
    setRightPanelContent(null);
    setIsChatPanelOpen(true);
    onQuickChatUserSelected(userId);
    setActiveUserId(userId);
    setPopperAnchor(event.currentTarget);
  }, [
    setIsChatPanelOpen,
    setActiveUserId,
    onQuickChatUserSelected,
    setRightPanelContent,
    userId,
  ]);

  const handleQuickChatClose = useCallback(() => {
    onQuickChatUserSelected('');
  }, [
    onQuickChatUserSelected,
  ]);

  useEffect(() => {
    if (!quickChatUser) {
      setPopperAnchor(null);
    }
  }, [
    quickChatUser,
  ]);

  const shouldRenderPopper = false;

  const unreadBadge = unreadCount > 0 && (
    <UnreadBadge>
      <UnreadBadgeText>{unreadCount}</UnreadBadgeText>
    </UnreadBadge>
  );

  const renderChatButton = () => {
    if (textIconButton) {
      return (
        <StyledButton onClick={handleQuickChatClick}>
          <StyledChatTextIcon />
          {texts.caption}
        </StyledButton>
      );
    }

    if (iconButton) {
      return (
        <ChatIconButton onClick={handleQuickChatClick} aria-label={texts.caption}>
          <StyledChatLightIcon variant={iconVariant} />
          {unreadBadge}
        </ChatIconButton>
      );
    }

    if (smallButton) {
      return (
        <PrimaryButton
          onClick={handleQuickChatClick}
          icon={<StyledChatIcon variant={variants.SECONDARY} />}
          variant="warning"
          size="small"
        >
          {texts.caption}
        </PrimaryButton>
      );
    }

    return (
      <ChatButton onClick={handleQuickChatClick}>
        <StyledChatIcon variant={iconVariant} />
        {text}
      </ChatButton>
    );
  };

  return (
    <Container>
      {renderChatButton()}
      {shouldRenderPopper && (
        <StyledPopper
          open={shouldRenderPopper}
          anchorEl={popperAnchor}
          placement={popperPlacement}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'viewport',
                padding: 8,
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleQuickChatClose}>
            <QuickChatContainer />
          </ClickAwayListener>
        </StyledPopper>
      )}
    </Container>
  );
};

ChatButtonComponent.propTypes = {
  userId: PropTypes.string.isRequired,
  text: PropTypes.string,
  popperPlacement: PropTypes.string,
  iconButton: PropTypes.bool,
  smallButton: PropTypes.bool,
  iconVariant: PropTypes.string,
  textIconButton: PropTypes.bool,
};

ChatButtonComponent.defaultProps = {
  text: '',
  popperPlacement: 'left-start',
  iconButton: false,
  smallButton: false,
  textIconButton: false,
  iconVariant: variants.PRIMARY,
};

export default ChatButtonComponent;
